<template lang="pug">
.treatment-table(v-click-outside="onClickOutside" @click="onClickTable" :key="tableKey")
  v-data-table.table-custom(:headers="headers" :items="treatments" hide-default-footer disable-sort :loading="isLoading" fixed-header @click:row="onClickRow" loader-height="2" :items-per-page="treatments.length")
    //- Product Item
    template(v-slot:item.productItem="{ item, index }")
      treatment-product-item(:key="productItemKey" :item="item" :isOTC="isOTC" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onChangeProductItem(item, index)" @editDocument="allowDocumentEdit ? onEditDocument(item) : onEditDocumentNotAllowed()" @addBulk="openAddBulkMedicineDialog = true")
    //- Active Medication
    template(v-slot:item.active="{ item, index }")
      treatment-active-medication(:item="item" :patient="patient" :treatments="treatments" :parentActiveMedications="parentActiveMedications" @updateActiveMedications="$emit('updateActiveMedications')")
    //- Quantity
    template(v-slot:item.quantity="{ item }")
      treatment-quantity(v-if="item.priceTier || (item.productItem && item.productItem.type === 'documents') || item.isChild" :item="item" @changed="onUpdateItem")
    //- Price-tiers
    template(v-slot:item.priceTiers="{ item }")
      treatment-price-tier(v-if="item.productItem" :key="priceTierKey" :item="item" :autofocus="selectedTreatment && selectedTreatment.index === item.index && selectedTreatment.autofocus === 'priceTier'" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onChangePriceTier(item)")
    //- Rate
    template(v-slot:item.rate="{ item }")
      treatment-rate(v-if="item.isParent || item.priceTier || (item.productItem && item.productItem.type === 'documents')" :key="rateKey" :item="item" :allowedPriceEdit="allowedPriceEdit" @changed="onUpdateItem")
    //- Amount
    template(v-slot:item.amount="{ item }")
      treatment-amount(v-if="item.isParent || item.priceTier" :key="amountKey" :item="item")
    //- Dosage
    template(v-slot:item.dosage="{ item }")
      treatment-dosage(v-if="item.productItem" :item="item" @changed="onUpdateItem")
    //- Instruction
    template(v-slot:item.instruction="{ item }")
      treatment-instruction(v-if="item.productItem" :key="instructionKey" :item="item" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onUpdateItem")
    //- Frequency
    template(v-slot:item.frequency="{ item }")
      treatment-frequency(v-if="item.productItem" :key="frequencyKey" :item="item" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onUpdateItem")
    //- Duration
    template(v-slot:item.duration="{ item }")
      treatment-duration(v-if="item.productItem" :key="durationKey" :item="item" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onUpdateItem")
    //- Indication
    template(v-slot:item.indication="{ item }")
      treatment-indication(v-if="item.productItem" :key="indicationKey" :item="item" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onUpdateItem")
    //- Precaution
    template(v-slot:item.precaution="{ item }")
      treatment-precaution(v-if="item.productItem" :key="precautionKey" :item="item" @dropdownOpenClosed="onDropdownOpenClosed($event)" @changed="onUpdateItem")
    //- Delete
    template(v-slot:item.action="{ item }")
      template(v-if="!item.isChild && (isCompletedItem(item) || item.productItem)")
        v-btn(icon @click.stop="onDeleteTreatment(item)")
          v-icon(size="18" :color="item.error ? 'danger500' : ''") $yzi-trash-03
    //- No data
    template(v-slot:no-data)
      div
  .dialogs
    delete-confirmation-dialog(v-if="openDeleteConfirmationDialog" :open.sync="openDeleteConfirmationDialog" :loading="isDeletingTreatment" @cancel="onCancelDeleteTreatment" @delete="onConfirmDeleteTreatment")
      delete-confirmation-details(:selectedTreatment="selectedTreatment")
    add-medical-document-dialog-old(v-if="openAddMedicalDocumentDialog" :open.sync="openAddMedicalDocumentDialog" :leadPid="leadPid" :patient="patient" :selectedTreatment="selectedTreatment" :newDocument="newDocument" :parentDiagnosis="parentDiagnosis" @save="onMedicalDocumentSaved")
    edit-not-allowed-dialog(v-if="openEditNotAllowedDialog" :open.sync="openEditNotAllowedDialog" )
    add-bulk-medicine-dialog(v-if="openAddBulkMedicineDialog" :open.sync="openAddBulkMedicineDialog" @insert="handleAddBulkTreatment")
    bulk-add-medication-error-dialog(v-if="openBulkAddMedicationErrorDialog" :open.sync="openBulkAddMedicationErrorDialog" :errorItems="bulkTreatmentErrors" @cancel="handleCancelBulkAddMedicationErrorDialog")
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { debounce } from '@/utils'
import TreatmentProductItem from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentProductItem.vue'
import TreatmentActiveMedication from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentActiveMedication.vue'
import TreatmentQuantity from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentQuantity.vue'
import TreatmentPriceTier from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentPriceTier.vue'
import TreatmentRate from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentRate.vue'
import TreatmentAmount from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentAmount.vue'
import TreatmentDosage from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentDosage.vue'
import TreatmentInstruction from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentInstruction.vue'
import TreatmentFrequency from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentFrequency.vue'
import TreatmentDuration from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentDuration.vue'
import TreatmentPrecaution from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentPrecaution.vue'
import TreatmentIndication from '@/components/clinic/consultation/treatments/treatmentTableItems/TreatmentIndication.vue'
import DeleteConfirmationDetails from '@/components/clinic/consultation/treatments/treatmentTableItems/DeleteConfirmationDetails.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import EditNotAllowedDialog from '@/components/clinic/consultation/EditNotAllowedDialog.vue'
import AddMedicalDocumentDialogOld from '@/components/clinic/consultation/treatments/AddMedicalDocumentDialogOld.vue'
import AddBulkMedicineDialog from '@/components/clinic/consultation/treatments/AddBulkMedicineDialog.vue'
import BulkAddMedicationErrorDialog from '@/components/clinic/consultation/treatments/BulkAddMedicationErrorDialog.vue'

const INCOMPLETE_TREATMENT_ERROR_MESSAGE = 'Please complete the unfinished treatment plan first'

export default {
  components: {
    TreatmentProductItem, TreatmentActiveMedication, TreatmentIndication, TreatmentQuantity, TreatmentPriceTier, TreatmentRate, TreatmentAmount, TreatmentDosage, TreatmentInstruction, TreatmentFrequency,
    TreatmentDuration, TreatmentPrecaution, DeleteConfirmationDialog, DeleteConfirmationDetails, AddMedicalDocumentDialogOld, EditNotAllowedDialog, AddBulkMedicineDialog, BulkAddMedicationErrorDialog
  },
  props: {
    leadPid: {
      type: String,
      required: true 
    },
    parentTreatments: {
      type: Array,
      default: () => {
        return []
      }
    },
    parentDefaultPayment: {
      type: Object,
      default: null
    },
    parentDiagnosis: {
      type: Array,
      default: () => {
        return []
      }
    },
    // tableHeight: {
    //   type: String,
    //   default: '250px'
    // },
    parentIncompletedDocuments: {
      type: Array,
      default: () => {
        return []
      }
    },
    invoiceNo: {
      default: null
    },
    isOTC: {
      type: Boolean
    },
    patient: {
      type: Object,
      default: () => {
        return {}
      }
    },
    registrationStatus: {
      type: String,
      default: null
    },
    parentActiveMedications: {
      type: Object,
      default: () => {
        return {
          isFetching: false,
          isError: false,
          data: [],
        }
      }
    }
  },
  data:() => ({
    isLoading: false,
    headers: [
      { text: '#', value: 'number', width: 50 },
      { text: 'Item', value: 'productItem', width: 250 },
      { text: 'Active', value: 'active', align: 'center' },
      { text: 'Qty', value: 'quantity', width: 100 },
      { text: 'Price Tier', value: 'priceTiers', width: 200 },
      { text: 'Rate', value: 'rate', width: 150 },
      { text: 'Amount', value: 'amount', width: 150 },
      { text: 'Dosage', value: 'dosage', width: 165 },
      { text: 'Instruction', value: 'instruction', width: 200 },
      { text: 'Frequency', value: 'frequency', width: 200 },
      { text: 'Duration', value: 'duration', width: 200 },
      { text: 'Indication', value: 'indication', width: 200 },
      { text: 'Precaution', value: 'precaution', width: 200 },
      { text: '', value: 'action' },
    ],
    treatments: [],
    tableKey: 0,
    treatmentsKey: 0,
    selectedTreatment: null,
    isDeletingTreatment: false,
    openDeleteConfirmationDialog: false,
    openEditNotAllowedDialog : false,
    openAddMedicalDocumentDialog: false,
    newDocument: false,
    productItemKey: 0,
    priceTierKey: 0,
    rateKey: 0,
    amountKey: 0,
    instructionKey: 0,
    frequencyKey: 0,
    durationKey: 0,
    precautionKey: 0,
    indicationKey: 0,
    isOpeningDropdown: false,
    openAddBulkMedicineDialog: false,
    bulkTreatmentErrors: [],
    isHandlingBulkTreatments: false,
    openBulkAddMedicationErrorDialog: false,
  }),
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
        company: 'getCompany',
        isDoctor: 'user/isDoctor',
        hqSelectedCompany: 'hq/getSelectedCompany',
        isHq: 'hq/getIsHq'
      }),
    canAddNew() {
      return this.parentTreatments.length === this.treatments.length
    },
    incompletedDocuments: {
      get() {
        return this.parentIncompletedDocuments
      },
      set(newValue) {
        this.$emit('update:parentIncompletedDocuments', newValue)
      }
    },
    incompletedTreatments () {
      // Reactivity for deep array items
      this.treatmentsKey
      return this.treatments.filter(treatment => treatment.inProgress)
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true

      return false
    },
    allowedPriceEdit () {
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
    allowDocumentEdit () {
      if (this.company.genes.find(gene => gene.feature === 'lock_document_edit_treatment_plan') && !this.isAdminOrDoctor) return false

      return true
    },
    tableHeight () {
      let height = 100
      height += Math.round(Number(this.treatments.length) * 48)
      return height
    }
  },
  watch: {
    parentTreatments() {
      if (this.isHandlingBulkTreatments) {
        this.mapData()
      }
    },
    'parentActiveMedications.data'() {
      this.mapData()
    }
  },
  mounted () {
    this.headers.map(item => item['class'] = 'text-table-header')
    if (this.parentTreatments.length === 0) {
      this.onAddRow(false)
    } else {
      this.mapData()
    }
    // Only inject if not otc or status not completed
    if (!this.isOTC && this.registrationStatus !== 'completed' && this.company.genes.find(gene => gene.feature === 'auto_inject_consultation')) {
      this.autoInjectConsultationFee()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    mapData () {
      this.treatments = []
      let mapIdx = 0
      this.parentTreatments.forEach((treatment, idx) => {
        const number = idx + 1
        let result = {
          id: treatment.id,
          active: false,
          index: mapIdx++,
          number,
          inProgress: false,
          editable: false,
          productItem: { 
            ...treatment.product,
          },
          rate: treatment.price,
          quantity: treatment.quantity,
          priceTier: {
            name: treatment.extra_data?.price_tier_name
          }
        }
        if (treatment.bundles.length) {
          result.bundles = [ ...treatment.bundles ]
          result.isParent = true
          delete result.priceTier
        }
        if (this.isMedicineType(treatment.product)) {
          result.productItem.medication = { ...treatment.extra_data, id: treatment.product.medication.id }
          result.active = this.parentActiveMedications.data.some(med => med.medication.id === treatment.product.medication.id)
        }
        if (treatment.product.type === 'documents') {
          result.productItem.documentData = { ...treatment.extra_data }
          result.completed = true
          if (this.incompletedDocuments.length > 0) {
            const found = this.incompletedDocuments.find(doc => doc === number)
            if (found) {
              result.completed = false
            }
          }
        }
        this.treatments.push(result)
        if (treatment.bundles.length) {
          treatment.bundles.forEach((bundle) => {
            let data = {
              id: bundle.id,
              index: mapIdx++,
              inProgress: false,
              editable: false,
              productItem: { 
                ...bundle.product,
              },
              rate: 0,
              quantity: bundle.quantity,
              isChild: true,
              parentId: result.id
            }
            if (this.isMedicineType(bundle.product)) {
              data.productItem.medication = { ...bundle.extra_data, id: bundle.product.medication.id }
            }
            this.treatments.push(data)
          })
        }
      })
      this.onAddRow(false)
    },
    async onChangeProductItem(item, index, isAutoInject = false, retrowError = false) {
      // Update array reactivity
      this.treatmentsKey++
      this.productItemKey++
      // Logger
      const activityItem = {...item}
      if (activityItem.priceTier) delete activityItem.priceTier // remove this due to priceTier from prev item
      /**
       * Update inserted items activity even before post request
       * Because there is possibility they don't setup price tier yet and causing the item to not be able to save
      **/ 
      if (isAutoInject) {
        this.updateAutoInjectActivityLog(null, { item:activityItem, row: index + 1 }, null, null, null)
      } else {
        this.updateActivityLog(null, { item:activityItem, row: index + 1 }, null, null, null)
      }
      // Sanitized data if has id
      const id = this.treatments[index]?.id ?? null
      if (id) {
        /** 
         * Delete current item if has id (requested by backendersss) to make it POST everytime
         * There's a reason for this, but please trust your backend
         * */

        // Remove bundle children
        if (this.treatments[index]?.isParent) {
          this.treatments = this.treatments.filter(f => !(f.parentId && f.parentId === id))
          this.treatments.map((treatment, idx) => treatment.index = idx)
        }
        try {
          await this.$axios.delete(`/leads/${this.leadPid}/products/${id}`)
        } catch (error) {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        }
        const allowedProperties = ['index', 'number', 'editable', 'productItem']
        for (const key in this.treatments[index]) {
          if (!allowedProperties.includes(key)) {
            delete this.treatments[index][key]
          }
        }
      }
      // Handling price tiers
      const priceUpdated = await this.updatePriceTier(item, isAutoInject, retrowError)
      if (!priceUpdated) {
        item.editable = true
        item.inProgress = true
        this.selectedTreatment = { ...item, autofocus: 'priceTier' }
      } else {
        item.editable = false
        item.inProgress = false
        this.selectedTreatment = { ...item }
      }
      // Handling Bundle
      if (item.productItem.bundle) {
        item.isParent = true
        item.bundles = item.productItem.bundle.treatments
        item.rate = item.productItem.price
        item.quantity = 1
        this.updateItem(item)
      }
      // Handling documents
      if (item.productItem.type === 'documents') {
        this.newDocument = true
        this.openAddMedicalDocumentDialog = true
      }
      this.priceTierKey++
    },
    async updatePriceTier(item, isAutoInject = false, retrowError = false) {
      // Skip if product bundle
      if (item.isChild || item.isParent || item.productItem.bundle) {
        return true
      }
      // Skip document without pricing enabled
      if (item.productItem.type === 'documents' && !item.productItem.document_template.price_tier_enabled) {
        item.rate = 0
        item.quantity = 1
        return true
      }
      let params = { 
        company: this.isHq ? this.hqSelectedCompany?.id : this.company.id,
        product: item.productItem.id
      }
      const isPanelPayment = this.parentDefaultPayment && this.parentDefaultPayment.type !== 'self-pay'
      if (isPanelPayment) {
        params.payment_methods = this.parentDefaultPayment.panel.id
      } else {
        params.payment_channel = 'self-pay'
      }
      try {
        const { data } = await this.$axios.get('/product_pricetiers', { params })
        if (data.count > 0) {
          item.priceTier = data.results[0]
          await this.onChangePriceTier(item, isAutoInject, retrowError)
          return true
        }
        return false
      // eslint-disable-next-line no-empty
      } catch (error) {
        if (retrowError) throw error

        return false
      }
    },
    async onChangePriceTier (item, isAutoInject = false, retrowError = false) {
      this.priceTierKey++
      this.rateKey++
      item.rate = item.priceTier.price || 0 // default to zero if price tier doesn't have any price
      item.quantity = item.quantity ?? item.productItem?.medication?.default_qty ?? 1
      item.inProgress = false
      this.selectedTreatment = null
      await this.updateItem({...item}, isAutoInject, retrowError)
    },
    async updateItem (item, isAutoInject = false, retrowError = false) {
      this.amountKey++
      this.instructionKey++
      this.frequencyKey++
      this.durationKey++
      this.precautionKey++
      this.indicationKey++
      if (this.isCompletedItem(item)) {
        const payload = this.sanitizePayload(item)
        let resp
        try {
          this.isLoading = true
          delete this.treatments[item.index].error
          if (payload.id) {
            const id = payload.id
            delete payload.id
            const endpoint = `/leads/${this.leadPid}/products/${id}`
            resp = await this.$axios.patch(endpoint, payload)
            if (isAutoInject) {
              this.updateAutoInjectActivityLog(endpoint, payload, resp.data, 'PATCH', id)
            } else {
              this.updateActivityLog(endpoint, payload, resp.data, 'PATCH', id)
            }
            if (this.treatments[item.index] && this.treatments[item.index].productItem) {
              this.treatments[item.index].productItem.inventory_qty = resp.data.product.inventory_qty
            }
          } else {
            const endpoint = `/leads/${this.leadPid}/products`
            resp = await this.$axios.post(endpoint, payload)
            if (isAutoInject) {
              this.updateAutoInjectActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id)
            } else {
              this.updateActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id)
            }
            if (this.treatments[item.index] && this.treatments[item.index].productItem) {
              this.treatments[item.index].productItem.inventory_qty = resp.data.product.inventory_qty
            }
            // Handling bundle
            if (item.isParent) {
              let bundles = resp.data.bundles
              let childrenArray = []
              bundles.forEach(bundle => {
                let data = {
                  id: bundle.id,
                  index: null,
                  inProgress: false,
                  editable: false,
                  rate: 0,
                  quantity: bundle.quantity,
                  isChild: true,
                  parentId: resp.data.id
                }
                let product = item.bundles.find(f => f.id === bundle.product.id)
                if (product) {
                  data.productItem = product
                  if (this.isMedicineType(product)) {
                    data.productItem.medication = { ...bundle.extra_data, id: product.medication.id }
                  }
                }
                childrenArray.push(data)
              })
              this.treatments.splice((item.index + 1), 0, ...childrenArray)
              this.treatments.map((treatment, idx) => treatment.index = idx)
            }
          }
          this.treatments[item.index].id = resp.data.id
          // Check add new row
          if (this.treatments.every(treatment => treatment.id)) {
            this.onAddRow(false)
          }
          if (this.parentActiveMedications.data?.length > 0 && ['medication', 'consumable'].includes(item.productItem.type)) {
            this.treatments[item.index].active = this.parentActiveMedications.data.some(med => med.medication.id === item.productItem.medication.id)
          }
          this.$emit('changed')
        } catch (error) {
          this.$set(this.treatments[item.index], 'error', true)
          if (retrowError) throw error
          if (error.response?.status === 400 && error.response?.data?.code === '444101') {
            this.showMessage(['error', 'Item quantity more than available stock. Please adjust the quantity'])
            return
          }
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        } finally {
          this.isLoading = false
        }
      }
    },
    onUpdateItem: debounce(function (item, isAutoInject = false, retrowError = false) {
      this.updateItem(item, isAutoInject, retrowError)
    }, 750),
    sanitizePayload (item) {
      let result = {
        id: item.id,
        product: item.productItem.id,
        quantity: item.quantity,
        price: item.rate,
      }
      // If product bundle
      if (item.productItem.bundle && !item.id) {
        let bundles = []
        item.productItem.bundle.treatments.forEach(treatment => {
          let data = { product: treatment.id, quantity_per_set: treatment.quantity_per_set }
          if (this.isMedicineType(treatment)) {
            if (treatment.medication.instruction_enabled) {
              data.extra_data = {
                dosage_unit: treatment.medication.dosage_unit,
                dosage_value: treatment.medication.dosage_value,
                frequency: treatment.medication.frequency,
                duration: treatment.medication.duration,
                instruction: treatment.medication.instruction,
                precaution: treatment.medication.precaution,
                indication: treatment.medication.indication,
              }
            }
          }
          bundles.push(data)
        })
        result.bundles = [...bundles]
      }
      // if has price tier
      if (item.priceTier) {
        result.extra_data = {
          price_tier: item.priceTier?.id,
          price_tier_name: item.priceTier?.name,
        }
      }
      if (this.isMedicineType(item.productItem)) {
        result.extra_data = {
          ...result.extra_data,
          dosage_unit: item.productItem.medication.dosage_unit,
          dosage_value: item.productItem.medication.dosage_value,
          frequency: item.productItem.medication.frequency,
          duration: item.productItem.medication.duration,
          instruction: item.productItem.medication.instruction,
          precaution: item.productItem.medication.precaution,
          indication: item.productItem.medication.indication,
        }
      }
      if (item.productItem.type === 'documents') {
        result.extra_data = {
          ...item.productItem.documentData,
          ...result.extra_data
        }
      }
      return result
    },
    async autoInjectConsultationFee () {
      this.isLoading = true
      const TYPE = 'consultation'
      // Check if consultation already existed, never inject new consultation
      const hasTreatmentConsultations = this.treatments.some(treatment => treatment?.productItem?.type === TYPE)
      if (hasTreatmentConsultations) {
        this.isLoading = false
        return
      }
      // Fetch treatment product type consultation and append to treatment table
      try {
        let params = {
          page: 1,
          page_size: 35,
          search: TYPE,
          status: 'published'
        }
        const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
        const { data } = await this.$axios.get(`/clinics/${companySlug}/treatment_products`, { params })
        // Return early
        if (data.count === 0) return
        // Get consultations and append if found
        const consultations = data.results.filter(result => result.type === TYPE)
        if (consultations.length > 0) {
          const index = this.treatments.length - 1
          const data = {
            index,
            productItem: consultations[0]
          }
          await this.onChangeProductItem(data, index, true)
          // Update treatment table with id and data
          this.treatments[index] = {...this.treatments[index], ...data}
          // Rerender table
          this.$nextTick(() => {
            this.tableKey++
          })
        }
      // eslint-disable-next-line no-empty
      } catch (error) {
      } finally {
        this.isLoading = false
      }
    },
    async handleAddBulkTreatment (items) {
      this.isHandlingBulkTreatments = true
      const newItems = items.map((item, itemIdx) => ({index: (this.treatments.length - 1) + itemIdx, number: this.treatments.length + itemIdx, productItem: item}))
      for (let index = 0; index < newItems.length; index++) {
        try {
          const data = newItems[index]
          const { idx } = data
          await this.onChangeProductItem(data, idx, true, true)
          // Update treatment table with id and data
          this.treatments[idx] = {...this.treatments[idx], ...data}
        } catch (error) {
          if (error.response) this.bulkTreatmentErrors.push({ item: newItems[index], error})
        }
      }
      // Rerender table
      this.$emit('addBulkTreatment')
      this.openAddBulkMedicineDialog = false
      if (this.bulkTreatmentErrors.length > 0) {
        this.openBulkAddMedicationErrorDialog = true
      } else {
        this.showMessage(['success', 'Successfully added all items'])
      }
    },
    handleCancelBulkAddMedicationErrorDialog () {
      this.bulkTreatmentErrors = []
      this.isHandlingBulkTreatments = false
    },
    onMedicalDocumentSaved (number, items, completed) {
      if (completed) {
        const index = this.incompletedDocuments.findIndex(doc => doc === number)
        if (index >= 0) {
          this.incompletedDocuments.splice(index, 1)
        }
      } else {
        this.incompletedDocuments.push(number)
      }
      this.treatments.map(async (treatment, treatmentIdx) => {
        if (treatment.number === number) {
          this.treatments[treatmentIdx].productItem.documentData = items
          this.treatments[treatmentIdx].completed = completed
          await this.onUpdateItem(treatment)
        }
      })
      this.$emit('changed')
    },
    onEditDocument (item) {
      this.selectedTreatment = item
      this.newDocument = false
      this.openAddMedicalDocumentDialog = true
    },
    onEditDocumentNotAllowed () {
      this.openEditNotAllowedDialog = true
    },
    onClickTable() {
      if (this.canAddNew) {
        this.onAddRow()
      }
    },
    onClickRow(item) {
      // Update deep array reactivity
      this.treatmentsKey++
      // Prevent from editing new item if has incompleted treatments
      if (this.incompletedTreatments.length > 0 && this.incompletedTreatments[0].index !== item.index) {
        this.showMessage(['error', INCOMPLETE_TREATMENT_ERROR_MESSAGE])
        return
      }
      item.editable = true
      this.treatments.filter(treatment => treatment.index !== item.index).map(treatment => treatment.editable = false)
    },
    onClickOutside() {
      if (this.isOpeningDropdown || this.openDeleteConfirmationDialog) return

      // Update deep array reactivity
      this.treatmentsKey++
      // Prevent from close editing mode if incomplete
      if (this.incompletedTreatments.length > 0) {
        this.showMessage(['error', INCOMPLETE_TREATMENT_ERROR_MESSAGE])
        return
      }
      if (this.incompletedTreatments.length === 0) {
        this.treatments.map(treatment => treatment.inProgress ? null : treatment.editable = false)
      }
    },
    onDropdownOpenClosed (state) {
      this.isOpeningDropdown = state
    },
    onAddRow(editable=true) {
      const newItem = {
        id: null,
        index: this.treatments.length,
        number: this.treatments.filter(f => f.number).length + 1,
        editable,
        active: false
      }
      this.treatments.push(newItem)
    },
    onDeleteTreatment(item) {
      this.selectedTreatment = item
      if (item.id) {
        this.openDeleteConfirmationDialog = true
        return
      }
      this.postDeleteTreatment(item.index)
    },
    onCancelDeleteTreatment() {
      this.selectedTreatment = null
      this.openDeleteConfirmationDialog = false
    },
    async onConfirmDeleteTreatment() {
      try {
        const deletedIndex = this.selectedTreatment.index
        this.isDeletingTreatment = true
        const id = this.selectedTreatment.id
        const endpoint = `/leads/${this.leadPid}/products/${id}`
        await this.$axios.delete(endpoint)
        this.updateActivityLog(endpoint, null, null, 'DELETE', id)
        this.$emit('changed')
        this.postDeleteTreatment(deletedIndex, id)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDeletingTreatment = false
        this.openDeleteConfirmationDialog = false
      }
    },
    postDeleteTreatment(deletedIndex, deletedId) {
      // this.treatments.splice(deletedIndex, 1)
      this.treatments = this.treatments.filter(f => !(f.index === deletedIndex || (f.parentId && f.parentId === deletedId)))
      this.treatments.filter(f => f.number).map((treatment, idx) => treatment.number = idx + 1)
      this.treatments.map((treatment, idx) => treatment.index = idx)
      if (deletedIndex === this.treatments.length) {
        this.onAddRow(true)
      }
    },
    isMedicineType(product) {
      if (product.medication) {
        return true
      }
      return false
    },
    isCompletedItem (item) {
      if (item.isChild) {
        return item.productItem && item.quantity
      }
      return item.productItem && item.rate >= 0 && item.quantity
    },
    updateAutoInjectActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: this.invoiceNo ? 'invoice' : 'consultation',
        activity: this.invoiceNo ? `Auto inject invoice (#${this.invoiceNo})` : `Auto inject consultation (${this.leadPid})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: this.invoiceNo ? 'invoice' : 'consultation',
        activity: this.invoiceNo ? `Update invoice (#${this.invoiceNo})` : `Update consultation (${this.leadPid})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
  }
}
</script>

<style scoped>
::v-deep table > tbody > tr > td:nth-child(1),
::v-deep table > thead > tr > th:nth-child(1) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  z-index: 10;
  background: white;
}
::v-deep table > tbody > tr > td:nth-child(2),
::v-deep table > thead > tr > th:nth-child(2) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 50px;
  z-index: 10;
  background: white;
}
::v-deep table > tbody > tr > td:nth-child(14),
::v-deep table > thead > tr > th:nth-child(14) {
  position: sticky !important;
  position: -webkit-sticky !important;
  right: 0;
  z-index: 10;
  background: white;
}
::v-deep table > thead > tr {
  position: sticky !important;
  position: -webkit-sticky !important;
  top: 0;
  z-index: 11;
  background: white;
}

::v-deep .table-custom > .v-data-table__wrapper {
  padding-bottom: 50px;
}
</style>